<template>
    <div class="widget contact-properties">
        <div class="widget-header" v-if="showWidgetTitle">
            <h3 class="widget-title">{{ $t("message.contactInfo") }}</h3>
        </div>
        <div class="widget-content">
            <div v-for="(element, index) in sortedContactProperties" :key="element._id" class="drag-row">
                <ContactDetailItem
                    :contact="contact"
                    :label="getLabel(element)"
                    :value="getPropertyValue(element._id)"
                    :inputType="element.type"
                    :options="element.options || []"
                    @edit="updatePropertyValue(element._id, $event)"
                    @add="addPropertyValue(element._id, $event)"
                    @delete="deleteProperty(element._id)"
                >
                    <template #icon>
                        <div class="material-icons drag-handle">drag_indicator</div>
                    </template>
                    <template #actions>
                        <button v-if="index > 0" class="action-button move-btn" @click="moveUp(index)">
                            <span class="material-icons">arrow_upward</span>
                        </button>
                        <button
                            v-if="index < sortedContactProperties.length - 1"
                            class="action-button move-btn"
                            @click="moveDown(index)"
                        >
                            <span class="material-icons">arrow_downward</span>
                        </button>
                    </template>
                </ContactDetailItem>
            </div>
            <div v-if="sortedContactProperties.length === 0">{{ $t("message.noPropertiesAdded") }}</div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import ContactDetailItem from "./ContactDetailItem.vue";
import log2 from "../../../utils/log2";
import StringUtils from "../../../utils/StringUtils";

const log = log2("ContactProperties");

export default {
    name: "ContactProperties",
    components: {
        ContactDetailItem,
    },
    props: {
        contact: {
            type: Object,
            required: true,
        },
        showWidgetTitle: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            sortedContactProperties: [],
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            allContactProperties: (state) => state.contactProperty.data || [],
        }),
    },
    created() {
        this.initializeSortedProperties();
    },
    watch: {
        sortedContactProperties: {
            handler(newValue) {
                const order = newValue.reduce((acc, prop, index) => {
                    acc[prop._id] = index;
                    return acc;
                }, {});
                localStorage.setItem("contactPropertiesOrder", JSON.stringify(order));
            },
            deep: true,
        },
    },
    methods: {
        ...mapActions({
            createContactProperty: "visitor/createContactProperty",
            createNewContactProperty: "contactProperty/createContactProperty",
            updateMultipleKeyContact: "chat/updateMultipleKeyContact",
            deleteContactProperty: "visitor/deleteContactProperty",
        }),
        getKey() {
            return StringUtils.hashCodeObj(this.sortedContactProperties);
        },
        initializeSortedProperties() {
            const savedOrder = JSON.parse(localStorage.getItem("contactPropertiesOrder")) || {};
            const allProperties = this.allContactProperties.map((p) => ({ ...p, value: this.getPropertyValue(p._id) }));

            const propertyOrder = allProperties.reduce((acc, prop, index) => {
                acc[prop.name] = savedOrder[prop._id] !== undefined ? savedOrder[prop._id] : index;
                return acc;
            }, {});
            log.debug("Özellik sıralaması:", propertyOrder);

            this.sortedContactProperties = allProperties.sort((a, b) => {
                const orderA = savedOrder[a._id] !== undefined ? savedOrder[a._id] : Infinity;
                const orderB = savedOrder[b._id] !== undefined ? savedOrder[b._id] : Infinity;
                return orderA - orderB;
            });

            console.log(
                "Sıralanmış özellikler:",
                this.sortedContactProperties.map((p) => p.name)
            );
        },
        getLabel(property) {
            return property.label || property.name;
        },
        getPropertyValue(propertyId) {
            const property = this.contact.contactProperties.find((cp) => this.isPropertyMatch(cp, propertyId));
            return property ? property.value : null;
        },
        isPropertyMatch(cp, propertyId) {
            if (typeof cp.property === "object" && cp.property !== null) {
                return cp.property._id === propertyId;
            } else {
                return cp.property === propertyId;
            }
        },
        async updatePropertyValue(propertyId, newValue) {
            await this.createVisitorAttribute({
                key: propertyId,
                value: newValue,
            });
        },
        async addPropertyValue(propertyId, newValue) {
            await this.createVisitorAttribute({
                key: propertyId,
                value: newValue,
            });
        },
        async createVisitorAttribute(form) {
            try {
                let propertyId = form.key;
                if (form.newKey) {
                    const newContactProperty = await this.createNewContactProperty({
                        name: form.newKey,
                        label: form.newKey,
                        type: "text",
                    });
                    propertyId = newContactProperty._id;
                }

                const res = await this.createContactProperty({
                    visitorId: this.contact._id,
                    payload: {
                        property: propertyId,
                        value: form.value,
                    },
                });

                const updatedProperties = [...this.contact.contactProperties];
                const existingPropertyIndex = updatedProperties.findIndex((p) => this.isPropertyMatch(p, propertyId));

                if (existingPropertyIndex !== -1) {
                    updatedProperties[existingPropertyIndex] = res;
                } else {
                    updatedProperties.push(res);
                }

                await this.updateMultipleKeyContact({
                    contactId: this.contact._id,
                    set: {
                        contactProperties: updatedProperties,
                    },
                });

                this.$message.success(this.$t("message.propertyAddedUpdated"));
            } catch (error) {
                log.error("Error in createVisitorAttribute:", { error });
                this.$message.error(this.$t("message.propertyError"));
            }
        },
        async deleteProperty(propertyId) {
            try {
                await this.deleteContactProperty({
                    visitorId: this.contact._id,
                    contactPropertyId: propertyId,
                });

                const updatedProperties = this.contact.contactProperties.filter(
                    (cp) => !this.isPropertyMatch(cp, propertyId)
                );

                await this.updateMultipleKeyContact({
                    contactId: this.contact._id,
                    set: {
                        contactProperties: updatedProperties,
                    },
                });

                this.$message.success(this.$t("message.propertyDeleted"));
            } catch (error) {
                log.error("Error in deleteProperty:", { error });
                this.$message.error(this.$t("message.propertyDeleteError"));
            }
        },
        moveUp(index) {
            if (index > 0) {
                const item = this.sortedContactProperties.splice(index, 1)[0];
                this.sortedContactProperties.splice(index - 1, 0, item);
                this.saveSortOrder();
            }
        },
        moveDown(index) {
            if (index < this.sortedContactProperties.length - 1) {
                const item = this.sortedContactProperties.splice(index, 1)[0];
                this.sortedContactProperties.splice(index + 1, 0, item);
                this.saveSortOrder();
            }
        },
        saveSortOrder() {
            const order = this.sortedContactProperties.reduce((acc, prop, index) => {
                acc[prop._id] = index;
                return acc;
            }, {});
            localStorage.setItem("contactPropertiesOrder", JSON.stringify(order));

            const savedOrder = JSON.parse(localStorage.getItem("contactPropertiesOrder")) || {};
            const allProperties = this.allContactProperties.map((p) => ({ ...p, value: this.getPropertyValue(p._id) }));

            const propertyOrder = allProperties.reduce((acc, prop, index) => {
                acc[prop.name] = savedOrder[prop._id] !== undefined ? savedOrder[prop._id] : index;
                return acc;
            }, {});
            log.debug("Özellik sıralaması:", propertyOrder);
        },
    },
};
</script>

<style lang="scss" scoped>
.widget {
    background-color: #fff;

    .widget-header {
        padding: 15px;
        border-bottom: 1px solid #e0e0e0;

        .widget-title {
            font-size: 18px;
            font-weight: bold;
            color: #333;
            margin: 0;
        }
    }

    .widget-content {
        padding: 15px;
    }
}

.contact-properties {
    display: flex;
    flex-direction: column;
    gap: 10px;
    position: relative;

    .drag-rows {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    .drag-row {
        padding: 5px;
        border-radius: 4px;
        transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

        //&:hover {
        //    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.16), 0 1px 2px rgba(0, 0, 0, 0.23);
        //}
    }

    .ghost {
        opacity: 0.5;
        background: #c8ebfb;
    }

    .drag-handle {
        cursor: move;
        padding: 5px;
        margin-right: 5px;

        &:hover {
            background-color: #e0e0e0;
        }
    }

    .action-button {
        cursor: pointer;
        border: none;
        background: none;
        padding: 1px;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background-color 0.3s;

        .material-icons {
            font-size: 12px;
            transition: color 0.3s;
        }

        &.move-btn {
            .material-icons {
                color: #757575;
            }

            &:hover {
                background-color: rgba(0, 0, 0, 0.05);
            }
        }
    }
}
</style>
